import type { ScriptCheckItem, ScriptChecks, SidePanelTab } from './types';

export const TAB_SHORTCUTS: { value: SidePanelTab; label: string }[] = [
  { value: 'course-info', label: 'Course info' },
  { value: 'script', label: 'Script' },
  { value: 'photos', label: 'Photos' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'objectives', label: 'Objectives' },
  { value: 'chat', label: 'Chat' },
];

export const INSTRUCTOR_DEFAULT_TAB: SidePanelTab = 'course-info';
export const STUDENT_DEFAULT_TAB: SidePanelTab = 'chat';

export const INSTRUCTOR_CONFIRMATION_TEXT =
  "I understand that I am entering a course hearing. I understand that my role in a course hearing is to use the provided rubric and my subject matter expertise to verify the student's mastery of the course goals and objectives as they are currently stated, and not to depart from the course goals and objectives in my assessment. I also understand that I'm not permitted to instruct or tutor students during the assessment portion of the course hearing. To the best of my ability, I promise to conduct this course hearing objectively and fairly.";

export const STUDENT_PREAMBLE_TEXT =
  "Congratulations! You are entering the last step of completing a course: a course hearing. In a course hearing, you will meet with an expert who will ask you questions about the course goals and objectives to determine your mastery. The task of the expert is to use their subject matter expertise to determine what goals and objectives you have mastered in the course, and if there are goals or objectives that you have not yet mastered, to tell you what they are, and to point you to review resources and make sure you are clear on what your next steps would be. A course hearing is designed to verify your current mastery of the course goals and objectives. It is not a tutoring session. By entering the course hearing, you give your consent to being recorded, and having that recording stored indefinitely by Newlane University for training and mastery-verification purposes.\n\nIf you disagree with the assessment, you can contest it by emailing: contact@newlaneuniversity.com with the subject 'contest course hearing'. This will trigger a review process.";

export const STUDENT_CONFIRMATIONS = [
  'I understand that I am entering a course hearing, and that the purpose of a course hearing is to verify my current mastery of the course goals and objectives.',
  'I understand that by entering this course hearing, I am giving my consent to Newlane University to record, review, and to store any recordings of this session for training and/or mastery verification purposes.',
  'I verify that I am who I am presenting myself to be.',
  'I verify that I have not received, nor will I receive any unauthorized assistance for this course hearing.',
] as const;

const introChecks: ScriptCheckItem[] = [
  {
    title: 'Confirm identity of the student and take a photo',
    text: 'E.g.,: "Hi is this ________? [Get verbal confirmation.] I\'m checking your profile pictures as well, [verify that student resembles profile pictures.] Is it ok if I take your picture? [get confirmation] [take picture]',
  },
  {
    title: 'Identify the course',
    text: 'This is a course hearing for [Course]',
  },
  {
    title: 'Identify yourself and expertise for course hearing',
    text: "My name is [Your name]. I'm acting as the host and judge for this course hearing. I hold [your advanced degree in relevant field]. You can view my profile by clicking on my name.",
  },
  {
    title: 'Ask if student has any questions (and answer as warranted)',
    text: 'Are you all clear on what a course hearing is? Do you have any questions before we begin?',
  },
];

const mainChecks: ScriptCheckItem[] = [
  {
    title: 'Explicitly assess and check off each course goal.',
    text: 'E.g., One of the course goals is Y (e.g., Explain how Ancient Western philosophy emerged in Greece), could you do that for me?',
  },
  {
    title:
      "Assess enough objectives, including any you have reason to doubt the student's mastery to determine your overall assessment.",
    text: "E.g., One of the objectives reads Z (e.g., summarize Plato's Apology), could you do that? etc. (be sure to ask about any objective the student has missed in previous assessments)",
  },
  {
    title: 'Communicate assessment outcome and rationale (Pass; or not yet)',
    text: "Thank you for your participation in this course hearing. My overall assessment is [Pass/not yet]. I'm satisfied you've mastered all the course goals and objectives/I'm not satisfied you've mastered all the course goals and objectives. I would recommend that you review X and Y objectives.",
  },
];

const outroChecks: ScriptCheckItem[] = [
  {
    title: 'Identify goals or objectives to review (if applicable)',
    text: '(If applicable) The objectives/goals you should review are X, Y, and Z.',
  },
  {
    title:
      "Congratulate student (even if they did not yet pass, it's a big deal to do a course hearing)",
    text: 'Congratulations for participating in this course hearing. Completing a course hearing is a major step in your degree program (regardless of the outcome).',
  },
  {
    title: 'Give guidance for next steps.',
    text: "Do you know which course you will be completing next? If you're not sure, I would recommend that you work on X course. [or if student isn't sure, and you are not sure, recommend they reach out to student support.] Also please take a minute while the hearing is fresh to complete the End of Course Evaluation Form. (It should take about 1-2 minutes).",
  },
  {
    title: "Close — I have explicitly assessed the student's mastery of each course goal.",
    text: 'Thank you and Good bye.',
  },
];

export const SCRIPT_CHECKS: ScriptChecks = {
  intro: introChecks,
  main: mainChecks,
  outro: outroChecks,
};

export const CHAT_POLL_INTERVAL_MS = 5000;
